import { VoiceSettings } from "./Voices.js";
import { useEffect, useState } from "react";
import {
  ROLE_LABELS,
  VOICE_SPEEDS,
  XAI_VOICES,
  can,
  type CampaignDetailResponse,
  type CampaignSettingsInput,
  type VoiceChannel,
  type CopyDmSource,
} from "@dm/shared";
import { DEFAULT_LATENCY, DEFAULT_BG_MODEL, DEFAULT_DM_MODEL, DM_MODELS, DM_BRAINS, LATENCY_LABELS, type DmBrain, type LatencySettings, type LatencySwitch } from "@dm/shared";
import { api } from "./api.js";
import { useApp } from "./appContext.js";

/** Tonight's campaign: settings (Host), members & roles, join requests and invites (Host + Co-hosts), leave/delete. */
export function CampaignPage() {
  const { me, reloadMe, fail } = useApp();
  const campaignId = me.campaign!.id;
  const [data, setData] = useState<CampaignDetailResponse | null>(null);
  const [channels, setChannels] = useState<VoiceChannel[]>([]);
  const [form, setForm] = useState<CampaignSettingsInput>({});
  const [saved, setSaved] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const d = await api.campaignDetail(campaignId);
    setData(d);
    const c = d.campaign;
    setForm({
      name: c.name, description: c.description, rulesMode: c.rulesMode, mature: c.mature, voiceSpeed: c.voiceSpeed,
      isPublic: c.isPublic, playersHomebrew: c.playersHomebrew, voiceChannelId: c.voiceChannelId, dmVoice: c.dmVoice,
      progression: c.progression, dmPersonality: c.dmPersonality, addressedOnly: c.addressedOnly, roleplaySocial: c.roleplaySocial, critFail: c.critFail, battleMap: c.battleMap, dmMaps: c.dmMaps, dmMapsPerSession: c.dmMapsPerSession,
    });
  };
  useEffect(() => {
    load().catch(fail);
    api.voiceChannels().then((r) => setChannels(r.channels), () => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <main className="muted">Loading…</main>;
  const who = { admin: me.isAdmin, role: data.role };
  const owner = can("editSettings", who);
  const staff = can("manageInvites", who);
  const set = (p: CampaignSettingsInput) => {
    setForm((f) => ({ ...f, ...p }));
    setSaved(false);
  };
  const save = () =>
    run(async () => {
      await api.updateCampaign(campaignId, form);
      await load();
      await reloadMe();
      setSaved(true);
    });
  const inviteUrl = (token: string) => `${window.location.origin}/#/invite/${token}`;
  const c = data.campaign;

  return (
    <main className="campaign-manage">
      <section className="panel">
        <h3>
          Settings {!owner && <span className="muted small">(only the Host can change these)</span>}
        </h3>
        <fieldset disabled={!owner || busy} className="settings-grid">
          <label className="field span-all">
            <span>Name</span>
            <input value={form.name ?? ""} maxLength={80} onChange={(e) => set({ name: e.target.value })} />
          </label>
          <label className="field span-all">
            <span>Description</span>
            <textarea rows={2} maxLength={2000} value={form.description ?? ""} onChange={(e) => set({ description: e.target.value })} />
          </label>
          <label className="field">
            <span>Rules mode</span>
            <select value={form.rulesMode} onChange={(e) => set({ rulesMode: e.target.value as "strict" | "cool" })}>
              <option value="strict">Strict 5e</option>
              <option value="cool">Rule of Cool</option>
            </select>
          </label>
          <label className="field">
            <span>Levelling</span>
            <select value={form.progression ?? "xp"} onChange={(e) => set({ progression: e.target.value as "xp" | "milestone" })}>
              <option value="xp">Experience points (XP)</option>
              <option value="milestone">Milestones (the DM or Host says when)</option>
            </select>
          </label>
          <label className="field">
            <span>DM voice speed</span>
            <select value={form.voiceSpeed} onChange={(e) => set({ voiceSpeed: Number(e.target.value) })}>
              {VOICE_SPEEDS.map((s) => (
                <option key={s} value={s}>
                  {s}x
                </option>
              ))}
            </select>
          </label>
          <div className="field">
            <span>DM voice</span>
            <div className="voice-pick">
              <select value={form.dmVoice ?? ""} onChange={(e) => set({ dmVoice: e.target.value || null })}>
                <option value="">Default (server setting)</option>
                {XAI_VOICES.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.id[0]!.toUpperCase() + v.id.slice(1)}: {v.description}
                  </option>
                ))}
              </select>
              <VoicePreview voice={form.dmVoice || "orion"} />
            </div>
          </div>
          <label className="field span-all">
            <span>Discord voice channel</span>
            <select value={form.voiceChannelId ?? ""} onChange={(e) => set({ voiceChannelId: e.target.value || null })}>
              <option value="">— not linked —</option>
              {c.voiceChannelId && !channels.some((ch) => ch.id === c.voiceChannelId) && (
                <option value={c.voiceChannelId}>#{c.voiceChannelName ?? c.voiceChannelId}</option>
              )}
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  #{ch.name} ({ch.guildName})
                </option>
              ))}
            </select>
          </label>
          <label className="check-inline">
            <input type="checkbox" checked={!!form.mature} onChange={() => set({ mature: !form.mature })} /> Mature content
          </label>
          <label className="check-inline">
            <input type="checkbox" checked={!!form.isPublic} onChange={() => set({ isPublic: !form.isPublic })} /> Public: anyone can request to join
          </label>
          <label className="check-inline">
            <input type="checkbox" checked={!!form.playersHomebrew} onChange={() => set({ playersHomebrew: !form.playersHomebrew })} /> Players can add homebrew
          </label>
          <label className="check-inline" title='Table talk stays private: speech only goes to the AI DM when it includes "Dungeon Master" or the speaker holds "Hold to talk to the DM" on the Table page.'>
            <input type="checkbox" checked={!!form.addressedOnly} onChange={() => set({ addressedOnly: !form.addressedOnly })} /> Only talk to the DM when addressed
            <span className="muted small"> (on by default: say "Dungeon Master" or hold the Table's talk button; other speech isn't sent or saved)</span>
          </label>
          <label className="check-inline" title="Before a Persuasion, Deception, Intimidation or Performance check with an NPC, the DM asks what your character actually says. A good line can earn advantage.">
            <input type="checkbox" checked={form.roleplaySocial !== false} onChange={() => set({ roleplaySocial: form.roleplaySocial === false })} /> Roleplay before social checks
            <span className="muted small"> (on by default: the DM asks for your character's words before Persuasion, Deception, Intimidation or Performance rolls; other checks are unchanged)</span>
          </label>
          <label className="check-inline" title="A natural 1 on an attack, check or save fails and brings a fitting, proportionate consequence for whoever rolled it (players and monsters). Off (default) = standard rules.">
            <input type="checkbox" checked={form.critFail === true} onChange={() => set({ critFail: form.critFail !== true })} /> Critical fail consequences on natural 1
            <span className="muted small"> (off by default; when on, a natural 1 on any d20 attack, check or save fails with a fun, fair consequence, e.g. a dropped weapon or falling prone; never deadly on its own)</span>
          </label>
          <label className="check-inline" title="The Map tab: a square grid with tokens for the party and monsters, fog of war, and a DM that knows where everyone stands.">
            <input type="checkbox" checked={form.battleMap !== false} onChange={() => set({ battleMap: form.battleMap === false })} /> <strong>Maps</strong> (battle map)
            <span className="muted small"> (on by default; the master switch: off hides the Map tab and the DM stops using maps and tokens)</span>
          </label>
          {form.battleMap !== false && (
            <label className="check-inline sub-option" title="For fights and tactical moments the DM makes a scene itself: a grid sized to the place, a Grok Imagine picture (priced per picture; plain grid if it fails or the cap is reached), the party at the entrance and the monsters placed.">
              <input type="checkbox" checked={form.dmMaps !== false} onChange={() => set({ dmMaps: form.dmMaps === false })} /> DM creates maps
              <span className="muted small"> (on by default: for fights and tactical scenes only; you can still swap the picture or delete the map)</span>
            </label>
          )}
          {form.battleMap !== false && form.dmMaps !== false && (
            <label className="sub-option" title="New maps the DM may make in one session (each with a Grok Imagine picture at the per-image price). Going back to an earlier scene doesn't count.">
              New maps per session{" "}
              <input type="number" min={0} max={30} style={{ width: "4em" }} value={form.dmMapsPerSession ?? 4}
                onChange={(e) => set({ dmMapsPerSession: Math.max(0, Math.min(30, Math.round(Number(e.target.value) || 0))) })} />
              <span className="muted small"> (default 4; reused scenes don't count; 0 = the DM only reuses maps you made)</span>
            </label>
          )}
        </fieldset>
        <label className="field span-all">
          <span>DM personality <span className="muted small">(how the DM talks and runs this game)</span></span>
          <textarea rows={3} maxLength={4000} disabled={!owner} value={form.dmPersonality ?? ""}
            placeholder="e.g. A wry old bard who loves foreshadowing, gives every tavern a smell, and lets clever plans work."
            onChange={(e) => set({ dmPersonality: e.target.value })} />
        </label>
        {owner && (
          <div className="quick-row">
            <button className="primary" disabled={busy} onClick={save}>
              Save settings
            </button>
            {saved && <span className="ok small">Saved</span>}
          </div>
        )}
      </section>

      <VoiceSettings key={`${c.ttsProvider}-${c.elevenVoiceId}-${c.npcVoiceMode}`} campaign={c} owner={owner} onSaved={() => load().catch(fail)} />
      <SpeedUps campaignId={campaignId} value={c.latency ?? DEFAULT_LATENCY} owner={owner} staff={can("runGame", who)} onSaved={() => load().catch(fail)} />
      {owner && <CopyDm campaignId={campaignId} onCopied={() => load().catch(fail)} />}

      <section className="panel">
        <h3>Members</h3>
        <ul className="member-list">
          {data.members.map((m) => (
            <li key={m.userId}>
              {m.avatarUrl ? <img src={m.avatarUrl} alt="" /> : <span className="avatar-blank" />}
              <span className="who">
                <strong>{m.displayName}</strong>
                <span className="muted small">{m.characterName ? `plays ${m.characterName}` : "no character yet"}</span>
              </span>
              {owner && m.role !== "host" ? (
                <select value={m.role} disabled={busy} onChange={(e) => run(async () => { await api.setMemberRole(campaignId, m.userId, e.target.value as "cohost" | "player"); await load(); })}>
                  <option value="cohost">Co-host</option>
                  <option value="player">Player</option>
                </select>
              ) : (
                <span className={`tag ${m.role === "player" ? "" : "host"}`}>{ROLE_LABELS[m.role]}</span>
              )}
              {owner && m.role !== "host" && (
                <button
                  className="danger small-btn"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Remove ${m.displayName}? Their character is retired (kept), and they lose access.`)) {
                      void run(async () => { await api.removeMember(campaignId, m.userId); await load(); });
                    }
                  }}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {staff && (
        <section className="panel">
          <h3>Join requests</h3>
          {data.joinRequests.length === 0 && <p className="muted small">No pending requests{c.isPublic ? "." : " (the campaign isn't public)."}</p>}
          <ul className="member-list">
            {data.joinRequests.map((r) => (
              <li key={r.id}>
                {r.avatarUrl ? <img src={r.avatarUrl} alt="" /> : <span className="avatar-blank" />}
                <span className="who">
                  <strong>{r.displayName}</strong>
                  <span className="muted small">{r.message || `asked ${new Date(r.createdAt).toLocaleString()}`}</span>
                </span>
                <button className="primary small-btn" disabled={busy} onClick={() => run(async () => { await api.decideJoin(campaignId, r.id, true); await load(); })}>
                  Approve
                </button>
                <button className="small-btn" disabled={busy} onClick={() => run(async () => { await api.decideJoin(campaignId, r.id, false); await load(); })}>
                  Deny
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {staff && (
        <section className="panel">
          <h3>Invite links</h3>
          <p className="muted small">Anyone with a link can join as a Player after logging in with Discord. Revoke a link to stop it working.</p>
          <ul className="invite-list">
            {data.invites.map((i) => (
              <li key={i.token}>
                <input readOnly value={inviteUrl(i.token)} onFocus={(e) => e.target.select()} />
                <button className="small-btn" onClick={() => void navigator.clipboard?.writeText(inviteUrl(i.token))}>
                  Copy
                </button>
                <span className="muted small">
                  {i.uses} use{i.uses === 1 ? "" : "s"} · by {i.createdByName ?? "?"}
                </span>
                <button className="danger small-btn" disabled={busy} onClick={() => run(async () => { await api.revokeInvite(campaignId, i.token); await load(); })}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
          <button disabled={busy} onClick={() => run(async () => { await api.createInvite(campaignId); await load(); })}>
            + New invite link
          </button>
        </section>
      )}

      {data.role && data.role !== "host" && (
        <section className="panel">
          <h3>Leave campaign</h3>
          <p className="muted small">Your character is retired and kept, in case you come back.</p>
          <button
            className="danger"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`Leave ${c.name}?`)) {
                void run(async () => { await api.leaveCampaign(campaignId); await reloadMe(); window.location.hash = "#/campaigns"; });
              }
            }}
          >
            Leave campaign
          </button>
        </section>
      )}

      {owner && (
        <section className="panel danger-zone">
          <h3>Delete campaign</h3>
          <p className="muted small">
            Deleted campaigns can be restored for 30 days (Campaigns → Recently deleted), then they're gone for good. Type <strong>{c.name}</strong> to confirm.
          </p>
          <div className="quick-row">
            <input value={confirm} placeholder={c.name} onChange={(e) => setConfirm(e.target.value)} />
            <button
              className="danger"
              disabled={busy || confirm.trim() !== c.name}
              onClick={() => run(async () => { await api.deleteCampaign(campaignId, confirm); await reloadMe(); window.location.hash = "#/campaigns"; })}
            >
              Delete campaign
            </button>
          </div>
        </section>
      )}
    </main>
  );
}

/** Plays a short sample of an xAI voice (generated once per voice on the server, then cached). */
function VoicePreview({ voice }: { voice: string }) {
  const [state, setState] = useState<"idle" | "loading" | "playing" | "error">("idle");
  const [audio] = useState(() => new Audio());
  useEffect(() => () => audio.pause(), [audio]);
  const play = async () => {
    if (state === "playing") {
      audio.pause();
      setState("idle");
      return;
    }
    setState("loading");
    audio.src = api.voicePreviewUrl(voice);
    audio.onended = () => setState("idle");
    audio.onerror = () => setState("error");
    try {
      await audio.play();
      setState("playing");
    } catch {
      setState("error");
    }
  };
  return (
    <button type="button" className="small" onClick={play} disabled={state === "loading"} title="Hear this voice">
      {state === "loading" ? "Loading…" : state === "playing" ? "■ Stop" : state === "error" ? "Preview failed, retry" : "▶ Preview"}
    </button>
  );
}

/** Copy DM: personality, voice, speed, rules mode and difficulty from another campaign you run (never memories). */
function CopyDm({ campaignId, onCopied }: { campaignId: number; onCopied: () => void }) {
  const [sources, setSources] = useState<CopyDmSource[] | null>(null);
  const [from, setFrom] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    api.copyDmSources(campaignId).then((r) => setSources(r.sources), () => setSources([]));
  }, [campaignId]);
  if (!sources?.length) return null;
  const pick = sources.find((x) => x.id === from);
  return (
    <section className="panel">
      <h3>Copy DM</h3>
      <p className="muted small">Bring over a DM you like from another campaign you run: its personality, voice, speaking speed, rules mode and difficulty. Memories are never copied.</p>
      <div className="quick-row">
        <select value={from} onChange={(e) => setFrom(Number(e.target.value))} aria-label="Copy the DM from">
          <option value={0}>Pick a campaign…</option>
          {sources.map((x) => (
            <option key={x.id} value={x.id}>{x.name}</option>
          ))}
        </select>
        <button
          disabled={!from}
          onClick={async () => {
            if (!pick || !window.confirm(`Replace this campaign's DM personality, voice, speed, rules mode and difficulty with ${pick.name}'s?`)) return;
            try {
              await api.copyDm(campaignId, from);
              setMsg(`Copied the DM from ${pick.name}.`);
              onCopied();
            } catch (err) {
              setMsg(err instanceof Error ? err.message : String(err));
            }
          }}
        >
          Copy DM
        </button>
        {pick && <span className="muted small">{pick.dmVoice ?? "default voice"} · {pick.rulesMode === "strict" ? "Strict 5e" : "Rule of Cool"} · {pick.difficulty}{pick.hasPersonality ? " · has a personality" : ""}</span>}
      </div>
      {msg && <p className="note small">{msg}</p>}
    </section>
  );
}

/** Per-campaign DM speed-ups (all on by default; turn one off to compare on the Playtest dashboard). */
function SpeedUps({ campaignId, value, owner, staff, onSaved }: { campaignId: number; value: LatencySettings; owner: boolean; staff: boolean; onSaved: () => void }) {
  const { fail } = useApp();
  const [v, setV] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(value); }, [value]);
  const toggle = (k: LatencySwitch) => save({ [k]: !v[k] });
  const save = async (patch: Partial<LatencySettings>) => {
    setBusy(true);
    try {
      setV(await api.setLatency(campaignId, patch));
      onSaved();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const [costs, setCosts] = useState<Record<string, number | null>>({});
  useEffect(() => {
    api.modelCosts(campaignId).then((r) => setCosts(Object.fromEntries(r.models.map((m) => [m.id, m.relative]))), () => undefined);
  }, [campaignId]);
  const costLabel = (id: string) => (costs[id] == null ? "" : ` (${costs[id]}x cost)`);
  const costHint = "Relative cost per token: the cheapest model is 1x. Worked out from the current price table, weighted by how the DM really uses tokens (new prompt text, cached prompt text, written output) over the last 30 days.";
  const saveModels = async (patch: { dmModel?: string; bgModel?: string }) => {
    setBusy(true);
    try {
      setV(await api.setModels(campaignId, patch));
      onSaved();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel">
      <h3>
        DM models and response speed {!owner && <span className="muted small">(only the Host can change these)</span>}
      </h3>
      <div className="dm-models">
        <label title={`The model that narrates and runs the game live (and its background thinking). ${costHint}`}>
          <strong>DM model</strong>{" "}
          <select value={v.dmModel ?? DEFAULT_DM_MODEL} disabled={!staff || busy} onChange={(e) => void saveModels({ dmModel: e.target.value })}>
            {DM_MODELS.map((m) => <option key={m.id} value={m.id} title={costHint}>{m.label}{costLabel(m.id)}{m.id === DEFAULT_DM_MODEL ? " · default" : ""}</option>)}
          </select>
        </label>
        <span className="muted small"> {DM_MODELS.find((m) => m.id === (v.dmModel ?? DEFAULT_DM_MODEL))?.help}</span>
        <br />
        <label title="Small jobs: session summaries and memory, level-up text, Help answers, matching a player's choice, monster plans, background thinking; also the Fast DM brain.">
          <strong>Background model</strong>{" "}
          <select value={v.bgModel ?? DEFAULT_BG_MODEL} disabled={!staff || busy} onChange={(e) => void saveModels({ bgModel: e.target.value })}>
            {DM_MODELS.map((m) => <option key={m.id} value={m.id} title={costHint}>{m.label}{costLabel(m.id)}{m.id === DEFAULT_BG_MODEL ? " · default" : ""}</option>)}
          </select>
        </label>
        <span className="muted small"> for small jobs (summaries, lookups, choice matching, monster plans). Changes apply on the next reply, no restart.{!staff && " Only the Host and Co-hosts can change these."}</span>
      </div>
      <p className="muted small">Ways the DM answers faster in voice. All on by default; turn one off to compare on the Playtest dashboard.</p>
      <fieldset disabled={!owner || busy} className="speedups">
        <div className="dm-brain">
          <strong>DM brain</strong>{" "}
          {DM_BRAINS.map((b) => (
            <label key={b.id} className="check-inline" title={b.help}>
              <input type="radio" name={`brain-${campaignId}`} checked={v.dmBrain === b.id} onChange={() => save({ dmBrain: b.id as DmBrain })} /> {b.label}
            </label>
          ))}
          <span className="muted small"> {DM_BRAINS.find((b) => b.id === v.dmBrain)?.help}</span>
        </div>
        {(Object.keys(LATENCY_LABELS) as LatencySwitch[]).map((k) => (
          <label key={k} className="check-inline" title={LATENCY_LABELS[k].help}>
            <input type="checkbox" checked={v[k]} onChange={() => toggle(k)} /> <strong>{LATENCY_LABELS[k].label}</strong>
            <span className="muted small"> {LATENCY_LABELS[k].help}</span>
          </label>
        ))}
      </fieldset>
    </section>
  );
}
